import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChartColumn,
  faCalculator,
  faWallet,
  faCircleInfo,
  faXmark,
  faFileContract,
  faListCheck,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";
import { useState, ReactElement } from "react";

const tabContent = {
  standard: {
    title: "Standard",
    price: "Starting at $2,500/quarter",
    description: "Manage your acquired business and investors with Mainshares.",
    features: {
      opcos: "1",
      includes: [
        "Business Dashboard",
        "Investor Updates",
        "Cap Table Management",
      ],
      valuations: "Annually",
      waterfallManagement: true,
      distributions: true,
      governanceAndConsents: true,
      waterfallsSupported: ["Common", "Preferred"],
    },
  },
  holdco: {
    title: "Hold-Co",
    price: "Starting at $5,000/quarter",
    description: "Manage your roll-up or hold-co with Mainshares.",
    features: {
      opcos: "3+",
      includes: [
        "Business Dashboard",
        "Investor Updates",
        "Cap Table Management",
      ],
      valuations: "Quarterly",
      waterfallManagement: true,
      distributions: true,
      governanceAndConsents: true,
      waterfallsSupported: [
        "Common",
        "Preferred",
        "MOIC Hurdles",
        "IRR Hurdles",
      ],
    },
  },
};

const Pricing = () => {
  const [activeTab, setActiveTab] = useState<"standard" | "holdco">("standard");

  const currentTab = tabContent[activeTab];

  return (
    <div className="max-w-screen md:max-w-2xl p-6 font-[Manrope]">
      {/* Tab Headers */}
      <div className="flex border-b mb-8 justify-center">
        <button
          className={`flex-1 px-8 py-4 text-xl font-semibold ${
            activeTab === "standard"
              ? "border-b-2 border-black text-black"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("standard")}
        >
          Standard
        </button>
        <button
          className={`flex-1 w-full px-8 py-4 text-xl font-semibold ${
            activeTab === "holdco"
              ? "border-b-2 border-black text-black"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("holdco")}
        >
          Hold-Co
        </button>
      </div>

      {/* Pricing Card */}
      <div className="bg-white rounded-lg p-4 md:p-8 shadow-lg">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-4">
          {currentTab.title}
        </h2>
        <p className="text-xl md:text-2xl text-gray-600 text-center mb-4">
          {currentTab.price}
        </p>
        <p className="text-lg md:text-xl text-center mb-8">
          {currentTab.description}
        </p>

        {/* Features List */}
        <div className="space-y-6 mb-8">
          <FeatureRow icon={getFeatureIcon("opcos")} label="Number of OpCos">
            <FeatureListSingle feature={currentTab.features.opcos.toString()} />
          </FeatureRow>
          <FeatureRow icon={getFeatureIcon("includes")} label="Includes">
            <FeatureList features={currentTab.features.includes} />
          </FeatureRow>
          <FeatureRow
            icon={getFeatureIcon("governance & consents")}
            label="Governance & Consents"
          >
            <FeatureListSingle
              feature={
                currentTab.features.governanceAndConsents ? (
                  <FontAwesomeIcon icon={faCheck} size="lg" />
                ) : (
                  <FontAwesomeIcon icon={faXmark} size="lg" />
                )
              }
            />
          </FeatureRow>
          <FeatureRow icon={getFeatureIcon("valuations")} label="Valuations">
            <FeatureListSingle feature={currentTab.features.valuations} />
          </FeatureRow>
          <FeatureRow
            icon={getFeatureIcon("waterfall management")}
            label="Waterfall Management"
          >
            <FeatureListSingle
              feature={
                currentTab.features.waterfallManagement ? (
                  <FontAwesomeIcon icon={faCheck} size="lg" />
                ) : (
                  <FontAwesomeIcon icon={faXmark} size="lg" />
                )
              }
            />
          </FeatureRow>
          <FeatureRow
            icon={getFeatureIcon("distributions")}
            label="Distributions"
          >
            <FeatureListSingle
              feature={
                currentTab.features.distributions ? (
                  <FontAwesomeIcon icon={faCheck} size="lg" />
                ) : (
                  <FontAwesomeIcon icon={faXmark} size="lg" />
                )
              }
            />
          </FeatureRow>
          <FeatureRow
            icon={getFeatureIcon("waterfalls supported")}
            label="Waterfalls Supported"
          >
            <FeatureList features={currentTab.features.waterfallsSupported} />
          </FeatureRow>

          {/* Add remaining feature rows similarly */}
        </div>

        <button className="w-full bg-[#14171F] text-white rounded-lg py-4 text-lg md:text-xl mb-4">
          Contact Sales
        </button>

        <p className="text-gray-600 text-center mb-8">
          Additional fees may apply
        </p>
      </div>
    </div>
  );
};

const FeatureRow = ({
  icon,
  label,
  children,
}: {
  icon: IconDefinition;
  label: string;
  children: ReactElement;
}) => (
  <div className="flex justify-between items-start">
    <div className="flex justify-between items-start w-full">
      <div className="flex items-center gap-3">
        <span>
          <FontAwesomeIcon icon={icon} size="lg" />
        </span>
        <span className="text-xl font-medium">{label}</span>
      </div>
      {children}
    </div>
  </div>
);

const FeatureList = ({ features }: { features: string[] }) => {
  return (
    <div className="text-right flex flex-col justify-end">
      {features.map((feature: string, index: number) => (
        <div key={index} className="text-lg font-semibold">
          {feature}
        </div>
      ))}
    </div>
  );
};

const FeatureListSingle = ({ feature }: { feature: string | ReactElement }) => {
  return (
    <div className="text-right flex flex-row justify-end">
      <div className="text-lg font-semibold">{feature}</div>
    </div>
  );
};

const getFeatureIcon = (feature: string): IconDefinition => {
  switch (feature.toLowerCase()) {
    case "opcos":
      return faBuilding;
    case "includes":
      return faListCheck;
    case "governance & consents":
      return faFileContract;
    case "valuations":
      return faChartColumn;
    case "waterfall management":
      return faCalculator;
    case "distributions":
      return faWallet;
    case "waterfalls supported":
      return faListCheck;
    default:
      return faCircleInfo;
  }
};

export default Pricing;
